/**
 * Single source of truth for the "active tenant" (org + client) persisted to
 * localStorage. The org context reads these keys to choose the default org/client
 * for the bare `/dashboard` resolver; the invite-accept flow writes them so a user
 * lands directly on the org/client they were just invited to.
 */

export const STORAGE_KEY_ACTIVE_ORG = 'nexmail_active_organization_id';
export const STORAGE_KEY_ACTIVE_CLIENT = 'nexmail_active_client_id';

// Pending-invite tenant, stashed when an unauthenticated user clicks an invite link
// and consumed after sign-in/sign-up accepts the invitation (see auth-store).
const PENDING_INVITE_ORG_KEY = 'pendingInvitationOrgId';
const PENDING_INVITE_CLIENT_KEY = 'pendingInvitationClientId';

/**
 * Persist the active org (always) and client (only when provided) so the next
 * resolution of `/dashboard` lands on this tenant.
 */
export function persistActiveTenant(orgId: string, clientId?: string | null): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY_ACTIVE_ORG, orgId);
  if (clientId) {
    localStorage.setItem(STORAGE_KEY_ACTIVE_CLIENT, clientId);
  }
}

/**
 * Parse an invitation's `clientIds` (a JSON-encoded array of ids) and return the
 * first id. Returns undefined when null/empty/malformed or for all-clients invites
 * (no specific client), in which case the caller should fall back to the org's first
 * accessible client.
 */
export function firstClientIdFromInvite(clientIds?: string | null): string | undefined {
  if (!clientIds) return undefined;
  try {
    const parsed = JSON.parse(clientIds);
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
      return parsed[0];
    }
  } catch {
    // Not valid JSON -- treat as no specific client.
  }
  return undefined;
}

/**
 * Stash the invited tenant for an unauthenticated user, alongside the
 * `pendingInvitationToken`. Consumed by `applyPendingInviteTenant` after the invite is
 * accepted during sign-in/sign-up.
 */
export function stashPendingInviteTenant(orgId: string, clientId?: string | null): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(PENDING_INVITE_ORG_KEY, orgId);
  if (clientId) {
    localStorage.setItem(PENDING_INVITE_CLIENT_KEY, clientId);
  }
}

/** Drop any stashed pending-invite tenant without promoting it (e.g. accept failed). */
export function clearPendingInviteTenant(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(PENDING_INVITE_ORG_KEY);
  localStorage.removeItem(PENDING_INVITE_CLIENT_KEY);
}

/**
 * If a pending-invite tenant was stashed, promote it to the active tenant and clear it.
 * Call after the pending invitation has been accepted. No-op when nothing is stashed.
 */
export function applyPendingInviteTenant(): void {
  if (typeof window === 'undefined') return;
  const orgId = localStorage.getItem(PENDING_INVITE_ORG_KEY);
  const clientId = localStorage.getItem(PENDING_INVITE_CLIENT_KEY);
  clearPendingInviteTenant();
  if (orgId) {
    persistActiveTenant(orgId, clientId);
  }
}
